import { Injectable, inject } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

import {
  DemoCredentialsModalComponent,
  PresidioDemoAccount,
} from 'ish-shared/components/login/auth-modals/demo-credentials-modal.component';
import {
  DemoErpModalComponent,
  PresidioDemoErpAccount,
} from 'ish-shared/components/login/auth-modals/demo-erp-modal.component';
import {
  DemoSsoModalComponent,
  PresidioDemoSsoIdentity,
} from 'ish-shared/components/login/auth-modals/demo-sso-modal.component';
import { ForgotPasswordModalComponent } from 'ish-shared/components/login/auth-modals/forgot-password-modal.component';
import { MfaVerificationModalComponent } from 'ish-shared/components/login/auth-modals/mfa-verification-modal.component';
import { RequestAccessModalComponent } from 'ish-shared/components/login/auth-modals/request-access-modal.component';
import {
  PresidioSalesRepAccountOption,
  SalesRepAccountSelectModalComponent,
} from 'ish-shared/components/login/auth-modals/sales-rep-account-select-modal.component';

export type PresidioAuthModalKind = 'forgot' | 'request-access' | 'mfa';

/**
 * Presidio UX — open Sign-in sub-flows as NgbModal overlays (forgot / request-access / MFA).
 * Also hosts DEMO-ONLY demo-credentials picker for the prototype.
 */
@Injectable({ providedIn: 'root' })
export class PresidioAuthModalService {
  private readonly modal = inject(NgbModal);

  private openRef: NgbModalRef | undefined;
  private openKind: PresidioAuthModalKind | undefined;
  /** Join concurrent login-gate account picks onto one modal (LoginPage remount race). */
  private salesAccountSelectPromise: Promise<string | undefined> | undefined;

  openForgot(): void {
    this.open('forgot', ForgotPasswordModalComponent, {
      scrollable: true,
      windowClass: 'prs-modal prs-auth-modal prs-auth-modal--forgot',
      ariaLabelledBy: 'prs-auth-modal-forgot-title',
    });
  }

  openRequestAccess(): void {
    this.open('request-access', RequestAccessModalComponent, {
      size: 'lg',
      scrollable: true,
      windowClass: 'prs-modal prs-auth-modal prs-auth-modal--request-access',
      ariaLabelledBy: 'prs-auth-modal-request-access-title',
    });
  }

  openMfa(): void {
    this.open('mfa', MfaVerificationModalComponent, {
      scrollable: true,
      windowClass: 'prs-modal prs-auth-modal prs-auth-modal--mfa',
      ariaLabelledBy: 'prs-auth-modal-mfa-title',
    });
  }

  /**
   * DEMO-ONLY — prototype credential picker. Does not displace production auth modals.
   */
  openDemoCredentials(): Promise<PresidioDemoAccount | undefined> {
    if (SSR) {
      return Promise.resolve(undefined);
    }

    const ref = this.modal.open(DemoCredentialsModalComponent, {
      centered: true,
      backdrop: true,
      keyboard: true,
      scrollable: true,
      windowClass: 'prs-modal prs-auth-modal prs-auth-modal--demo-credentials',
      ariaLabelledBy: 'prs-auth-modal-demo-title',
    });

    return ref.result.then(
      (account: PresidioDemoAccount) => account,
      () => undefined
    );
  }

  /**
   * DEMO-ONLY — Request access ERP picker (admin / CSR / not-found).
   */
  openDemoErpAccounts(): Promise<PresidioDemoErpAccount | undefined> {
    if (SSR) {
      return Promise.resolve(undefined);
    }

    const ref = this.modal.open(DemoErpModalComponent, {
      centered: true,
      backdrop: true,
      keyboard: true,
      scrollable: true,
      windowClass: 'prs-modal prs-auth-modal prs-auth-modal--demo-erp',
      ariaLabelledBy: 'prs-auth-modal-demo-erp-title',
    });

    return ref.result.then(
      (account: PresidioDemoErpAccount) => account,
      () => undefined
    );
  }

  /**
   * DEMO-ONLY — Microsoft SSO identity picker (has account vs SSO-only / no MyPresidio user).
   */
  openDemoSsoIdentities(): Promise<PresidioDemoSsoIdentity | undefined> {
    if (SSR) {
      return Promise.resolve(undefined);
    }

    const ref = this.modal.open(DemoSsoModalComponent, {
      centered: true,
      backdrop: true,
      keyboard: true,
      scrollable: true,
      windowClass: 'prs-modal prs-auth-modal prs-auth-modal--demo-sso',
      ariaLabelledBy: 'prs-auth-modal-demo-sso-title',
    });

    return ref.result.then(
      (identity: PresidioDemoSsoIdentity) => identity,
      () => undefined
    );
  }

  /**
   * DEMO MVP 8.2.1 — sales-rep affiliate account picker.
   * Login gate: dismissible false (cannot skip). Header switch: dismissible true.
   * Concurrent login-gate opens reuse the same modal promise so remounts cannot stack two pickers.
   */
  openSalesRepAccountSelect(
    accounts: PresidioSalesRepAccountOption[],
    options?: {
      dismissible?: boolean;
      currentCustomerNo?: string;
      titleKey?: string;
      hintKey?: string;
      continueKey?: string;
    }
  ): Promise<string | undefined> {
    if (SSR) {
      return Promise.resolve(undefined);
    }

    const dismissible = !!options?.dismissible;
    // Login-gate only: join an in-flight non-dismissible picker instead of opening a second one.
    if (!dismissible && this.salesAccountSelectPromise) {
      return this.salesAccountSelectPromise;
    }

    const ref = this.modal.open(SalesRepAccountSelectModalComponent, {
      centered: true,
      backdrop: dismissible ? true : 'static',
      keyboard: dismissible,
      scrollable: true,
      windowClass: 'prs-modal prs-auth-modal prs-auth-modal--sales-account',
      ariaLabelledBy: 'prs-auth-modal-sales-account-title',
    });
    ref.componentInstance.accounts = accounts;
    ref.componentInstance.dismissible = dismissible;
    if (options?.titleKey) {
      ref.componentInstance.titleKey = options.titleKey;
    }
    if (options?.hintKey) {
      ref.componentInstance.hintKey = options.hintKey;
    }
    if (options?.continueKey) {
      ref.componentInstance.continueKey = options.continueKey;
    }
    if (options?.currentCustomerNo) {
      ref.componentInstance.currentCustomerNo = options.currentCustomerNo;
    }

    const result = ref.result.then(
      (customerNo: string) => customerNo,
      () => undefined
    );
    if (!dismissible) {
      this.salesAccountSelectPromise = result.finally(() => {
        this.salesAccountSelectPromise = undefined;
      });
      return this.salesAccountSelectPromise;
    }
    return result;
  }

  openFromQueryParam(kind: string | null | undefined): void {
    if (kind === 'forgot') {
      this.openForgot();
    } else if (kind === 'request-access') {
      this.openRequestAccess();
    }
  }

  dismiss(): void {
    this.openRef?.dismiss();
    this.openRef = undefined;
    this.openKind = undefined;
  }

  private open(
    kind: PresidioAuthModalKind,
    component: new (...args: never[]) => unknown,
    options: {
      size?: 'sm' | 'lg' | 'xl';
      scrollable?: boolean;
      windowClass: string;
      ariaLabelledBy: string;
    }
  ): void {
    if (SSR) {
      return;
    }
    if (this.openKind === kind && this.openRef) {
      return;
    }
    this.dismiss();

    this.openRef = this.modal.open(component, {
      centered: true,
      backdrop: true,
      keyboard: true,
      size: options.size,
      scrollable: options.scrollable,
      windowClass: options.windowClass,
      ariaLabelledBy: options.ariaLabelledBy,
    });
    this.openKind = kind;

    this.openRef.result.finally(() => {
      if (this.openKind === kind) {
        this.openRef = undefined;
        this.openKind = undefined;
      }
    });
  }
}
